package top.wcpe.allincore.ui.web.asset

import kotlin.js.Promise

internal object JsAssetUriBackend : AssetUriBackend {
    override fun resolve(baseUri: String, canonicalPath: String): AssetUri = createUri(canonicalPath, baseUri)

    override fun parse(uri: String): AssetUri = createUri(uri, null)

    private fun createUri(value: String, baseUri: String?): AssetUri {
        val parsed = if (baseUri == null) {
            js("new globalThis.URL(value)")
        } else {
            js("new globalThis.URL(value, baseUri)")
        }
        return AssetUri(
            parsed.href as String,
            parsed.origin as String,
            parsed.pathname as String,
            parsed.search as String,
            parsed.hash as String,
            parsed.protocol as String,
        )
    }
}

internal class BrowserAssetObjectUrlBackend : AssetObjectUrlBackend {
    override fun create(bytes: ByteArray, mime: String): String {
        val blob = js("new globalThis.Blob([bytes], { type: mime })")
        return js("globalThis.URL.createObjectURL(blob)") as String
    }

    override fun revoke(url: String) {
        js("globalThis.URL.revokeObjectURL(url)")
    }
}

internal class BrowserAssetFetchBackend : AssetFetchBackend {
    override fun fetch(url: String, readLimit: AssetReadLimit): AssetFetchOperation {
        val controller = js("new globalThis.AbortController()")
        val result = Promise<AssetFetchResponse> { resolve, reject ->
            val options = js("({ signal: controller.signal })")
            val fetchPromise = js("globalThis.fetch(url, options)").unsafeCast<Promise<dynamic>>()
            fetchPromise.then<Unit>(
                onFulfilled = { response -> readResponse(response, readLimit, resolve, reject) },
                onRejected = { reject(AssetPolicyException("HTTP 资产读取失败")) },
            )
        }
        return AssetFetchOperation(result) { controller.abort() }
    }

    private fun readResponse(
        response: dynamic,
        readLimit: AssetReadLimit,
        resolve: (AssetFetchResponse) -> Unit,
        reject: (Throwable) -> Unit,
    ) {
        try {
            val contentLength = parseContentLength(response.headers.get("content-length") as String?)
            val reader = response.body?.getReader() ?: failBackend("HTTP 资产响应缺少可读内容流")
            if (contentLength != null && contentLength > readLimit.maximumBytes) {
                reader.cancel()
                failBackend("HTTP 资产 Content-Length 超过预算")
            }
            readChunk(response, reader, contentLength, readLimit, mutableListOf(), 0, resolve, reject)
        } catch (error: Throwable) {
            reject(error)
        }
    }

    private fun readChunk(
        response: dynamic,
        reader: dynamic,
        contentLength: Long?,
        readLimit: AssetReadLimit,
        chunks: MutableList<ByteArray>,
        totalBytes: Long,
        resolve: (AssetFetchResponse) -> Unit,
        reject: (Throwable) -> Unit,
    ) {
        reader.read().unsafeCast<Promise<dynamic>>().then<Unit>(
            onFulfilled = { result ->
                handleChunk(response, reader, result, contentLength, readLimit, chunks, totalBytes, resolve, reject)
            },
            onRejected = { reject(AssetPolicyException("HTTP 资产读取失败")) },
        )
    }

    private fun handleChunk(
        response: dynamic,
        reader: dynamic,
        result: dynamic,
        contentLength: Long?,
        readLimit: AssetReadLimit,
        chunks: MutableList<ByteArray>,
        totalBytes: Long,
        resolve: (AssetFetchResponse) -> Unit,
        reject: (Throwable) -> Unit,
    ) {
        if (result.done as Boolean) {
            resolve(toFetchResponse(response, contentLength, chunks, totalBytes))
            return
        }
        val chunk = typedArrayToByteArray(result.value)
        val updatedBytes = try {
            retainBrowserAssetChunk(chunks, chunk, totalBytes, readLimit)
        } catch (error: Throwable) {
            reader.cancel()
            reject(error)
            return
        }
        readChunk(response, reader, contentLength, readLimit, chunks, updatedBytes, resolve, reject)
    }

    private fun toFetchResponse(
        response: dynamic,
        contentLength: Long?,
        chunks: List<ByteArray>,
        totalBytes: Long,
    ): AssetFetchResponse = AssetFetchResponse(
        finalUrl = response.url as String,
        status = response.status as Int,
        declaredMime = response.headers.get("content-type") as String?,
        contentLength = contentLength,
        etag = response.headers.get("etag") as String?,
        lastModified = response.headers.get("last-modified") as String?,
        bytes = chunks.combine(totalBytes),
    )
}

internal fun browserFileSystemDirectoryHandle(handle: dynamic): AssetFileSystemDirectoryHandle =
    BrowserFileSystemDirectoryHandle(handle)

internal fun browserFileSystemFileHandle(handle: dynamic): AssetFileSystemFileHandle = BrowserFileSystemFileHandle(handle)

private fun dynamicPromise(value: dynamic): Promise<dynamic> = value.unsafeCast<Promise<dynamic>>()

private class BrowserFileSystemDirectoryHandle(private val handle: dynamic) : AssetFileSystemDirectoryHandle {
    override fun getDirectoryHandle(name: String): Promise<AssetFileSystemDirectoryHandle> =
        dynamicPromise(handle.getDirectoryHandle(name)).then<AssetFileSystemDirectoryHandle>(
            onFulfilled = { child: dynamic -> BrowserFileSystemDirectoryHandle(child) },
            onRejected = { reason: Throwable -> throw browserFileSystemFailure(reason) },
        )

    override fun getFileHandle(name: String): Promise<AssetFileSystemFileHandle> =
        dynamicPromise(handle.getFileHandle(name)).then<AssetFileSystemFileHandle>(
            onFulfilled = { file: dynamic -> BrowserFileSystemFileHandle(file) },
            onRejected = { reason: Throwable -> throw browserFileSystemFailure(reason) },
        )
}

private class BrowserFileSystemFileHandle(private val handle: dynamic) : AssetFileSystemFileHandle {
    override fun openSnapshot(prefixBytes: Int): Promise<AssetFileSystemSnapshot> =
        dynamicPromise(handle.getFile()).then<dynamic>(
            onFulfilled = { file: dynamic -> openBrowserSnapshot(file, prefixBytes) },
            onRejected = { reason: Throwable -> throw browserFileSystemFailure(reason) },
        ).unsafeCast<Promise<AssetFileSystemSnapshot>>()
}

private fun openBrowserSnapshot(file: dynamic, prefixBytes: Int): Promise<AssetFileSystemSnapshot> {
    val prefixBlob = file.slice(0, prefixBytes)
    return dynamicPromise(prefixBlob.arrayBuffer()).then<AssetFileSystemSnapshot>(
        onFulfilled = { buffer: dynamic -> BrowserFileSystemSnapshot(file, browserBytes(buffer)) },
        onRejected = { reason: Throwable -> throw browserFileSystemFailure(reason) },
    )
}

private class BrowserFileSystemSnapshot(
    private val file: dynamic,
    override val prefix: ByteArray,
) : AssetFileSystemSnapshot {
    override val declaredMime: String? = (file.type as String?).takeUnless { it.isNullOrBlank() }
    override val size: Long = (file.size as Number).toLong()
    override val lastModified: Long = (file.lastModified as Number).toLong()

    override fun read(maxBytes: Long): Promise<ByteArray> {
        if (size > maxBytes) return Promise.reject(AssetPolicyException("文件系统资产超过预算"))
        return dynamicPromise(file.slice(0, maxBytes.toDouble()).arrayBuffer()).then<ByteArray>(
            onFulfilled = { buffer: dynamic -> browserBytes(buffer) },
            onRejected = { reason: Throwable -> throw browserFileSystemFailure(reason) },
        )
    }
}

private fun browserBytes(buffer: dynamic): ByteArray {
    val view = js("new Int8Array(buffer)")
    return ByteArray(view.length as Int) { index -> (view[index] as Int).toByte() }
}

private fun browserFileSystemFailure(reason: dynamic): AssetFileSystemException {
    val failure = when (reason?.name as String?) {
        "NotFoundError" -> AssetFileSystemFailure.NOT_FOUND
        "NotAllowedError", "SecurityError" -> AssetFileSystemFailure.PERMISSION_REVOKED
        "AbortError" -> AssetFileSystemFailure.CANCELLED
        else -> AssetFileSystemFailure.READ_FAILED
    }
    return AssetFileSystemException(failure)
}

internal class BrowserJpegIntegrityChecker(private val objectUrls: AssetObjectUrlBackend) : AssetJpegIntegrityChecker {
    override fun check(bytes: ByteArray): AssetJpegCheckOperation = BrowserJpegCheckState(
        objectUrls,
        objectUrls.create(bytes, "image/jpeg"),
    ).operation()
}

private class BrowserJpegCheckState(private val objectUrls: AssetObjectUrlBackend, private val url: String) {
    private val image = js("new globalThis.Image()")
    private var finished = false
    private var revoked = false
    private var rejectCheck: ((Throwable) -> Unit)? = null

    fun operation(): AssetJpegCheckOperation {
        val result = Promise<Boolean> { resolve, reject ->
            rejectCheck = reject
            image.onload = { complete(resolve, image.naturalWidth > 0 && image.naturalHeight > 0) }
            image.onerror = { complete(resolve, false) }
            image.src = url
        }
        return AssetJpegCheckOperation(result, ::cancel)
    }

    private fun complete(resolve: (Boolean) -> Unit, valid: Boolean) {
        if (finished) return
        finished = true
        cleanup()
        resolve(valid)
    }

    private fun cancel() {
        if (finished) return
        finished = true
        cleanup()
        rejectCheck?.invoke(AssetPolicyException("JPEG 校验已取消"))
    }

    private fun cleanup() {
        image.onload = null
        image.onerror = null
        image.src = ""
        if (revoked) return
        revoked = true
        objectUrls.revoke(url)
    }
}

private fun parseContentLength(value: String?): Long? {
    if (value.isNullOrBlank()) return null
    return value.toLongOrNull() ?: failBackend("HTTP 资产 Content-Length 无效")
}

private fun typedArrayToByteArray(source: dynamic): ByteArray {
    val length = source.byteLength as Int
    return ByteArray(length) { index -> (source[index] as Int).toByte() }
}

internal fun retainBrowserAssetChunk(
    chunks: MutableList<ByteArray>,
    incoming: ByteArray,
    totalBytes: Long,
    readLimit: AssetReadLimit,
): Long {
    val updatedBytes = totalBytes + incoming.size
    val prefix = (chunks + incoming).aicrPrefix()
    if (updatedBytes > readLimit.byteLimitFor(prefix, complete = false)) {
        failBackend("HTTP 资产实际字节超过预算")
    }
    chunks += incoming
    return updatedBytes
}

private fun List<ByteArray>.aicrPrefix(): ByteArray {
    val prefix = ByteArray(AICR_MAGIC_BYTES)
    var copied = 0
    for (chunk in this) {
        val count = minOf(chunk.size, AICR_MAGIC_BYTES - copied)
        chunk.copyInto(prefix, copied, 0, count)
        copied += count
        if (copied == AICR_MAGIC_BYTES) return prefix
    }
    return prefix.copyOf(copied)
}

private fun List<ByteArray>.combine(totalBytes: Long): ByteArray {
    val combined = ByteArray(totalBytes.toInt())
    var offset = 0
    forEach { chunk ->
        chunk.copyInto(combined, offset)
        offset += chunk.size
    }
    return combined
}

private fun failBackend(message: String): Nothing = throw AssetPolicyException(message)
